/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { UserEmailArchiveAtom } from "../../../../../atoms/UserEmailArchiveAtom";
import { IUserIRoute } from "../../../../../generated/Api";
import useClickOutside from "../../../../../hook/useClickOutside";
import { SpaceBetween } from "../../../../../styles/general";
import { IOption } from "../../../../../types/CommonTypes";
import { User } from "../../../../../types/UserTypes";
import StyledAutocompleteSelect from "../../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledInput from "../../../../StyledInput";
import Switch from "../../../../Switch";
import FavoriteTimeSettings from "./FavoriteTime/FavoriteTimeSettings";
import LinkColorPicker from "./LinkColorPicker";

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 25px 0px;
  padding: 0px 22px;
  width: 600px;
  height: fit-content;

  @media screen and (max-width: 600px) {
    width: 100%;
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 5px;
`;

const SubjectDescpription = styled.div`
  color: #707070;
  font-size: 14px;
`;

const DELAYED_OPTIONS: IOption[] = [
  {
    value: "0",
    name: "No delay",
  },
  {
    value: "15",
    name: "15 seconds",
  },
  {
    value: "30",
    name: "30 seconds",
  },
  {
    value: "60",
    name: "1 minute",
  },
  {
    value: "300",
    name: "5 minutes",
  },
];

export const FONT_OPTIONS: IOption[] = [
  {
    value: "0",
    name: "Arial",
    font: "Arial",
  },
  {
    value: "1",
    name: "Courier New",
    font: "Courier New",
  },
  {
    value: "2",
    name: "Georgia",
    font: "Georgia",
  },
  {
    value: "3",
    name: "Helvetica",
    font: "Helvetica",
  },
  {
    value: "4",
    name: "Palatino",
    font: "Palatino",
  },
  {
    value: "5",
    name: "Roboto",
    font: "Roboto",
  },
  {
    value: "6",
    name: "Times New Roman",
    font: "Times New Roman",
  },
];

interface Props {
  user: IUserIRoute;
  setUser: (u: IUserIRoute) => void;
}

export default function GeneralSettings(props: Props): JSX.Element {
  const { user, setUser } = props;

  const { delayed, maxWidth, font } = user.emailOptions;
  const { extraCarriage, firstLineAsSubject, showPreviewLink } = user.options;

  const [limitWidth, setLimitWidth] = useState(maxWidth ? true : false);
  const [maxWidthInput, setMaxWidthInput] = useState(maxWidth ? maxWidth : 400);

  const [fontsSize] = useState<IOption[]>([
    { name: "14px", value: "14" },
    { name: "16px", value: "16" },
    { name: "18px", value: "18" },
    { name: "20px", value: "20" },
    { name: "22px", value: "22" },
  ]);

  const [selectedFontSize, setSelectedFontSize] = useState(
    fontsSize.find((ff) => `${ff.value}` === `${font.size}`)
  );

  const [selectFontOption, setSelectFontOption] = useState(
    FONT_OPTIONS.find((e) => e.name === user.emailOptions.font.family) ||
      FONT_OPTIONS[0]
  );

  const userEmailArchive = useRecoilValue(UserEmailArchiveAtom);

  function setValueInEmailOptions(key: keyof User["emailOptions"], value: any) {
    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        [key]: value,
      },
    });
  }

  function setValueInFontOptions(key: "family" | "size", value: any) {
    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        font: {
          ...user.emailOptions.font,
          [key]: value,
        },
      },
    });
  }

  function setValueInUserOptions(key: string, value: any) {
    setUser({
      ...user,
      options: {
        ...user.options,
        [key]: value,
      },
    });
  }
  const [showFontPicker, setShowFontPicker] = useState(false);

  const fontOptionsRef = useClickOutside(() => {
    setShowFontPicker(false);
  });

  //Remove MaxWidth if switch is false
  useEffect(() => {
    if (!limitWidth) {
      let emailOptionWithouMaxWidth = { ...user.emailOptions, maxWidth: null };

      setUser({
        ...user,
        emailOptions: emailOptionWithouMaxWidth,
      });
    } else {
      let emailOptionWithouMaxWidth = {
        ...user.emailOptions,
        maxWidth: maxWidthInput >= 400 ? maxWidthInput : 400,
      };

      setUser({
        ...user,
        emailOptions: emailOptionWithouMaxWidth,
      });
    }
  }, [limitWidth]);

  return (
    <>
      <FlexColumn>
        <Title>Send Delay</Title>

        <StyledAutocompleteSelect
          options={DELAYED_OPTIONS}
          selectedOption={DELAYED_OPTIONS.find((o) => o.value === delayed)}
          onOptionSelect={(evt) => {
            if (evt.value === "") return;
            setValueInEmailOptions("delayed", evt.value);
          }}
          placeholder={"Select delay"}
        />
      </FlexColumn>

      <FlexColumn>
        <SpaceBetween
          css={css`
            color: #707070;
            margin-top: 10px;
            padding-bottom: 12px;
            border-bottom: 1px solid #eceeee;
          `}
        >
          <Title>Show link preview</Title>
          <Switch
            toggled={!!showPreviewLink}
            whenToggled={(vle) => setValueInUserOptions("showPreviewLink", vle)}
          />
        </SpaceBetween>
      </FlexColumn>

      <FlexColumn>
        <Title>Copy / Paste Settings</Title>
        <SpaceBetween
          css={css`
            color: #707070;
            margin-top: 10px;
            padding-bottom: 12px;
            border-bottom: 1px solid #eceeee;
          `}
        >
          <SubjectDescpription>
            Always make the first line as email subject
          </SubjectDescpription>
          <Switch
            toggled={firstLineAsSubject}
            whenToggled={(vle) =>
              setValueInUserOptions("firstLineAsSubject", vle)
            }
          />
        </SpaceBetween>
        <SpaceBetween
          css={css`
            color: #707070;
            margin-top: 10px;
            padding-bottom: 12px;
            border-bottom: 1px solid #eceeee;
          `}
        >
          <SubjectDescpription>
            Always add extra carriage returns to format as plain text
          </SubjectDescpription>
          <Switch
            toggled={extraCarriage}
            whenToggled={(vle) => setValueInUserOptions("extraCarriage", vle)}
          />
        </SpaceBetween>
      </FlexColumn>

      <FlexColumn>
        <Title>Split Testing</Title>

        <SpaceBetween
          css={css`
            color: #707070;
            margin-top: 10px;
            padding-bottom: 12px;
            border-bottom: 1px solid #eceeee;
          `}
        >
          <SubjectDescpription>
            Enable split testing features{" "}
          </SubjectDescpription>
          <Switch
            toggled={!!user.emailOptions.splitTesting}
            whenToggled={(vle) => setValueInEmailOptions("splitTesting", vle)}
          />
        </SpaceBetween>
      </FlexColumn>

      {userEmailArchive && userEmailArchive.enabled && (
        <FlexColumn>
          <Title>Email Archiving</Title>

          <SpaceBetween
            css={css`
              color: #707070;
              margin-top: 10px;
              padding-bottom: 12px;
              border-bottom: 1px solid #eceeee;
            `}
          >
            <SubjectDescpription>
              Show this email on the archives{" "}
            </SubjectDescpription>
            <Switch
              toggled={user.emailOptions.showOnArchive}
              whenToggled={(vle) =>
                setValueInEmailOptions("showOnArchive", vle)
              }
            />
          </SpaceBetween>
        </FlexColumn>
      )}

      <FlexColumn>
        <Title>Email Default Styles</Title>

        <SpaceBetween
          css={css`
            color: #707070;
            margin-top: 10px;
            padding-bottom: 12px;
          `}
        >
          <SubjectDescpription>Limit email width </SubjectDescpription>
          <Switch
            toggled={limitWidth}
            whenToggled={(vle) => setLimitWidth(vle)}
          />
        </SpaceBetween>

        {limitWidth && (
          <StyledInput
            error={isNaN(maxWidthInput) || maxWidthInput < 400}
            min="400"
            step="1"
            type="number"
            name="someid"
            value={maxWidthInput}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setMaxWidthInput(value);
              setValueInEmailOptions("maxWidth", value);
            }}
            css={css`
              margin-top: 10px;
            `}
          />
        )}
      </FlexColumn>

      <FlexColumn>
        <Title>{"Default Fonts & Size"}</Title>

        <SpaceBetween
          css={css`
            color: #707070;
          `}
        >
          <div
            css={css`
              width: 100%;
              margin-right: 15px;
            `}
          >
            <StyledAutocompleteSelect
              options={FONT_OPTIONS}
              selectedOption={selectFontOption}
              onOptionSelect={(option) => {
                setValueInFontOptions("family", option.name);
                setSelectFontOption(option);
              }}
              placeholder={"Type or select a font option"}
            />
          </div>
          <StyledAutocompleteSelect
            options={fontsSize}
            selectedOption={selectedFontSize}
            onOptionSelect={(option) => {
              if (!option) {
                option = { name: "14px", value: "14" };
              }

              setSelectedFontSize(option);
              setValueInFontOptions("size", option.value);
            }}
            placeholder={"Type or select a font size"}
          />
        </SpaceBetween>
      </FlexColumn>

      <FavoriteTimeSettings user={user} setUser={setUser} />

      <LinkColorPicker user={user} setUser={setUser} />
    </>
  );
}
